import { ENTITY_LABELS } from "../../../utils/labelMapping";
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Spinner from "../../../Component/Spinner/Spinner";
import EmptyView from "../../../Component/EmptyView/EmptyView";
import { useContext } from "react";
import { DiscoveryContext } from "../discovery-context";
import { useEffect } from "react";
import {
  extractFocusAreas,
  handlePerformanceData,
  reachAndSentimentActions,
} from "../../../utils/actions-helpers";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { useDispatch } from "react-redux";
import { AuthContext } from "../../../context/auth-context";

// A function, not a static object, so the label re-resolves from ENTITY_LABELS on every render
// (a module-level object would freeze on whichever customer's labels loaded first).
function getTitleMapping() {
  return {
    reach: ENTITY_LABELS.rank,
    risk: "Performance",
    sentiment: "Sentiment",
  };
}
const ActionCard = ({
  number,
  title,
  descriptions,
  addSelectedItemsHandler,
}) => {
  const titleMapping = getTitleMapping();
  const [expandedIndexes, setExpandedIndexes] = useState({});
  const [selectedIndexes, setSelectedIndexes] = useState({});

  const handleToggle = (index) => {
    setExpandedIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleCheckboxChange = (index, desc) => {
    addSelectedItemsHandler(desc);
    setSelectedIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: 2,
        p: 2,
        backgroundColor: "#EFF4F8",
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          width: "110px",
          height: "80px",
          borderRadius: "8px",
          backgroundColor: "#DDE0E2",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          mr: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontSize: "15px", color: "#3C4041", fontWeight: "bold" }}
        >
          {titleMapping?.[title]}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#3C4041", fontSize: "15px" }}
        >
          {number}
        </Typography>
      </Box>

      {/* Right Accordion Section */}
      <Box sx={{ flex: 1 }}>
        {descriptions.map((desc, index) => (
          <Accordion
            key={index}
            expanded={!!expandedIndexes[index]}
            sx={{
              mb: 1,
              borderRadius: "4px",
              boxShadow: "none",
              "&:before": { display: "none" },
              backgroundColor: expandedIndexes[index] ? "#e8f4fa" : "#ffffff",
            }}
          >
            <AccordionSummary
              expandIcon={null}
              sx={{
                alignItems: "center",
                px: 1,
                py: 0,
                height: "10px",
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  justifyContent: "space-between",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  cursor: "pointer",
                  mr: 1,
                  transform: expandedIndexes[index]
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
                onClick={() => handleToggle(index)}
              >
                <ExpandMoreIcon />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#333333",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  {desc.text}
                </Typography>
              </Box>

              <Checkbox
                checked={!!selectedIndexes[index]}
                onChange={() => handleCheckboxChange(index, desc)}
              />
            </AccordionSummary>

            <AccordionDetails
              sx={{
                px: 1,
                py: 1,
                backgroundColor: "#f8f8f8",
              }}
            >
              {desc?.compTitles && desc?.compTitles.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#555555",
                      lineHeight: "18px",
                    }}
                  >
                    {desc?.compTitles?.map((item, idx) => (
                      <p style={{ fontSize: "14px" }} key={idx}>
                        {item}
                      </p>
                    ))}
                  </Typography>
                  <p></p>
                </>
              ) : null}

              {desc?.additionalInfo && desc?.additionalInfo.length ? (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#555555",
                    lineHeight: "18px",
                  }}
                >
                  <h6>Data Sources:</h6>
                  {desc?.additionalInfo?.map((item, idx) => (
                    <p key={idx}>{item}</p>
                  ))}
                </Typography>
              ) : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

const RealtimeActions = () => {
  const [actionsArray, setActionsArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const discoveryCtx = useContext(DiscoveryContext);
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const customerId = user?.customer_id || 0;

  function roundNumber(num) {
    return num % 1 < 0.5 ? Math.floor(num) : Math.ceil(num);
  }

  useEffect(() => {
    if (
      discoveryCtx.brand_scores.length === discoveryCtx.competitors.length &&
      Array.isArray(discoveryCtx.brand_scores) &&
      discoveryCtx.brand_scores.every(
        (obj) =>
          obj &&
          typeof obj === "object" &&
          obj.hasOwnProperty("total") &&
          typeof obj.total === "number" &&
          obj.total > 0
      )
    ) {
      const newData = reachAndSentimentActions(discoveryCtx.brand_scores) ?? [];
      setActionsArray((prev) => {
        const filteredArray = prev.filter(
          (item) => item.title !== "reach" && item.title !== "sentiment"
        );
        const updatedArray =
          newData.length > 0 ? [...filteredArray, ...newData] : filteredArray;
        return updatedArray;
      });
    }
  }, [
    discoveryCtx.brand_scores,
    discoveryCtx.competitors,
    discoveryCtx.entity,
  ]);

  useEffect(() => {
    const criterias = discoveryCtx?.category_dimensions;
    if (criterias?.data && criterias?.data.length > 0) {
      const purchaseCriteriaActions = extractFocusAreas(
        criterias?.data,
        ENTITY_LABELS.purchaseCriteria
      );
      const targetTitle = ENTITY_LABELS.purchaseCriteria; // or get it from another state/constant

      setActionsArray((prev) => {
        if (purchaseCriteriaActions) {
          const existingIndex = prev.findIndex(
            (item) => item.title === purchaseCriteriaActions.title
          );

          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = purchaseCriteriaActions;
            return updated;
          } else {
            return [...prev, purchaseCriteriaActions];
          }
        } else {
          // Remove object with targetTitle
          return prev.filter((item) => item.title !== targetTitle);
        }
      });
    }
  }, [discoveryCtx?.category_dimensions]);

  useEffect(() => {
    if (
      !discoveryCtx.core_attributes ||
      !discoveryCtx.core_attributes.category_averages ||
      Object.keys(discoveryCtx.core_attributes.category_averages).length === 0
    )
      return;

    const coreAttributesData = Object.keys(
      discoveryCtx.core_attributes.category_averages
    ).map((item) => {
      const scores = discoveryCtx.core_attributes.products.map((p) => ({
        name: p.name,
        score: p.attributes[item]?.score,
        explanation: p.attributes[item]?.explanation,
        sources: p.attributes[item]?.sources,
      }));
      return {
        name: item,
        scores,
      };
    });
    if (coreAttributesData && coreAttributesData.length > 0) {
      const brandPillarActions = extractFocusAreas(
        coreAttributesData,
        "Brand Pillar"
      );

      const targetTitle = "Brand Pillar";

      setActionsArray((prev) => {
        if (brandPillarActions) {
          const existingIndex = prev.findIndex(
            (item) => item.title === brandPillarActions.title
          );

          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = brandPillarActions;
            return updated;
          } else {
            return [...prev, brandPillarActions];
          }
        } else {
          // Remove object with targetTitle
          return prev.filter((item) => item.title !== targetTitle);
        }
      });
    }
  }, [discoveryCtx.core_attributes]);

  useEffect(() => {
    if (
      !discoveryCtx?.taxonomies ||
      !discoveryCtx?.taxonomies?.categories ||
      discoveryCtx?.taxonomies?.categories.length === 0
    )
      return;
    let categories = discoveryCtx?.taxonomies?.categories?.map((category) => ({
      name: category.name,
      products: discoveryCtx?.taxonomies?.products?.map((product) => {
        const subcategories = Object.values(
          product.scores[category.name]?.subcategories || {}
        );
        const totalScore = subcategories.length
          ? subcategories.reduce(
              (acc, item) => acc + (isNaN(+item.score) ? 0 : +item.score),
              0
            )
          : 0;
        const average = subcategories.length
          ? totalScore / subcategories.length
          : 0;

        return {
          name: product.name,
          score: roundNumber(average),
          subcategories:
            Object.keys(
              product.scores[category.name]?.subcategories || {}
            )?.map((subcat) => ({
              name: subcat,
              sources:
                product.scores[category.name]?.subcategories?.[subcat].sources,
              score:
                product.scores[category.name]?.subcategories?.[subcat].score,
              explanation:
                product.scores[category.name]?.subcategories?.[subcat]
                  .explanation,
            })) || [],
        };
      }),
    }));

    const headerOrder = discoveryCtx.competitors.map((h) => h.name);
    categories = categories.map((category) => ({
      ...category,
      products: category.products.sort(
        (a, b) => headerOrder.indexOf(a.name) - headerOrder.indexOf(b.name)
      ),
    }));

    if (categories?.length > 0) {
      const productDimActions = extractFocusAreas(
        categories,
        "Product Dimension"
      );
      const targetTitle = "Product Dimension"; // or get it from another state/constant

      setActionsArray((prev) => {
        if (productDimActions) {
          const existingIndex = prev.findIndex(
            (item) => item.title === productDimActions.title
          );

          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = productDimActions;
            return updated;
          } else {
            return [...prev, productDimActions];
          }
        } else {
          // Remove object with targetTitle
          return prev.filter((item) => item.title !== targetTitle);
        }
      });
    }
  }, [discoveryCtx.taxonomies]);

  const [selectedItems, setSelectedItems] = useState([]);
  const addSelectedItemsHandler = (desc) => {
    let currentSelectedItems = [...selectedItems];
    if (currentSelectedItems.some((item) => item.text === desc.text)) {
      currentSelectedItems = currentSelectedItems.filter(
        (item) => item.text !== desc.text
      );
    } else {
      currentSelectedItems.push(desc);
    }
    setSelectedItems(currentSelectedItems);
  };

  const saveActions = async (actions) => {
    if (!actions || actions.length === 0) return;

    setIsLoading(true);

    try {
      const actionList = await Promise.all(
        actions.map(async (obj) => {
          return {
            title: obj?.text,
            rationale: obj?.rationale,
            brand: discoveryCtx.entity,
            persona: "",
            geography: "",
            market: discoveryCtx.market,
            llm: discoveryCtx.llm,
            isProduct: discoveryCtx.type === "product",
            sources: obj?.additionalInfo?.map((url) => ({
              domain_url: url,
              fullUrl: null,
            })),
            compTitles: obj?.compTitles,
          };
        })
      );

      const payload = {
        action_json: actionList,
        is_archived: 0,
        is_active: 1,
      };

      dispatch(createActionMetric(customerId, payload))
        .then(() => {
          dispatch(fetchActionMetrics(customerId))
            .then(() => {
              toast.success("Action Added!", { autoClose: 700 });
            })
            .catch(() => {
              toast.error("Failed to fetch updated metrics!", {
                autoClose: 700,
              });
            });
        })
        .catch(() => {
          toast.error("Something went wrong while creating actions!", {
            autoClose: 700,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error("Error processing actions:", error);
      setIsLoading(false);
      toast.error("Something went wrong!", { autoClose: 700 });
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333333" }}>
          Focus Areas
        </Typography>
        <Button
          disabled={!selectedItems || selectedItems.length === 0}
          onClick={() => {
            saveActions(selectedItems);
          }}
          sx={{
            backgroundColor: "#4FD1C5",
            color: "#ffffff",
            fontWeight: "bold",
            textTransform: "none",
            px: 3,
            py: 1,
            "&:hover": { backgroundColor: "#5eb8e0" },
            borderRadius: "8px",
          }}
        >
          Add all to action list +
        </Button>
      </Box>

      <Grid container spacing={1}>
        {handlePerformanceData(actionsArray)?.map((action, index) => (
          <Grid item xs={12} key={index}>
            {action.number ? (
              <ActionCard
                number={action.number}
                title={action?.title || action?.mainTitle}
                descriptions={action.descriptions}
                addSelectedItemsHandler={addSelectedItemsHandler}
              />
            ) : null}
          </Grid>
        ))}
      </Grid>
      <ToastContainer />
    </Box>
  );
};

export default RealtimeActions;
